import { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { devices } from '../data/constants';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  margin: 16px 0;
  padding: 20px 24px;
  background: ${({ theme }) => theme.colors.opacityColor};
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 12px;
  text-align: center;
  animation: ${fadeIn} 0.4s ease-out;

  @media only screen and ${devices.md} {
    padding: 16px 20px;
  }
`;

const MessageText = styled.p`
  margin: 0;
  font-size: 15px;
  font-family: ${({ theme }) => theme.colors.ff};
  color: ${({ theme, $isError }) => $isError ? theme.colors.errorColor : theme.colors.cardText};
  font-weight: ${({ $isError }) => $isError ? '600' : '500'};
  animation: ${fadeIn} 0.3s ease-out;

  @media only screen and ${devices.md} {
    font-size: 14px;
  }
`;

const ProgressWrapper = styled.div`
  margin-top: 16px;
  animation: ${fadeIn} 0.4s ease-out;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 6px;
  background: ${({ theme }) => theme.colors.borderColor};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #4f46e5, #7c3aed);
  border-radius: 3px;
  transition: width 0.5s ease-out;
  width: ${({ $progress }) => $progress}%;
`;

const ProgressStatus = styled.p`
  margin: 8px 0 0 0;
  font-size: 13px;
  font-family: ${({ theme }) => theme.colors.ff};
  color: ${({ theme }) => theme.colors.cardText};
  opacity: 0.8;
`;

const MESSAGES = [
  // Original classics
  "Breaking into redacted government files",
  "Fetching file boxes covered in spider webs",
  "Dusting off county tax records",
  "Cross-referencing tax rolls by candlelight",
  "Decoding property tax hieroglyphics",
  "Consulting the real estate crystal ball",
  "Intercepting carrier pigeons from the tax office",
  "Bribing the county clerk with donuts",
  "Translating ancient deed documents",
  "Searching through musty basement archives",
  "Deciphering the tax collector's chicken scratch",
  "Waiting for the carrier pigeon to return",
  "Sending smoke signals to the title company",
  "Sneaking past the assessor's guard dog",
  "Running the numbers on a napkin",
  "Crunching numbers at ninja speed",
  "Sharpening the analysis katana",
  "Stress-testing this deal six ways to Sunday",
  // Property inspection humor
  "Measuring closets with a laser pointer",
  "Checking if the basement is haunted",
  "Counting how many times 'cozy' means tiny",
  "Decoding what 'charming' really means",
  "Inspecting the roof via satellite",
  "Asking the neighbors about the HOA drama",
  // Investor humor
  "Calculating how many avocado toasts this deal costs",
  "Running this by the imaginary investment committee",
  "Checking if the cap rate passes the vibe check",
  "Asking the spreadsheet if it believes in this deal",
  "Converting equity into theoretical jet skis",
  "Measuring ROI in units of early retirement",
  "Consulting the ancient scrolls of cash flow",
  "Summoning the ghost of deals past",
  "Checking if the numbers spark joy",
  "Running the 'would my accountant cry' test",
  "Calculating passive income in beach days",
  "Asking the magic 8-ball about appreciation",
  "Seeing if this deal survives the spouse test",
  "Checking if the rent covers the therapy bills",
  // Listing description humor
  "Translating 'investor special' to 'bring a hard hat'",
  "Decoding 'needs TLC' into actual dollar signs",
  "Wondering why there's no photo of the bathroom",
  "Reading between the lines of 'sold as-is'",
  "Investigating what 'unique layout' really means",
  // More analysis humor
  "Double-checking the math with a second napkin",
  "Stalking the neighborhood on Street View",
];

const shuffleArray = (arr) => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const QuirkyLoadingMessage = ({ error, progress, statusText, showProgress }) => {
  const [shuffled, setShuffled] = useState(() => shuffleArray(MESSAGES));
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    if (error) return;

    setShuffled(shuffleArray(MESSAGES));
    setMessageIndex(0);

    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % MESSAGES.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [error]);

  if (error) {
    return (
      <Container>
        <MessageText $isError>{error}</MessageText>
      </Container>
    );
  }

  return (
    <Container>
      <MessageText key={messageIndex}>{shuffled[messageIndex]}...</MessageText>
      {showProgress && (
        <ProgressWrapper>
          <ProgressBarContainer>
            <ProgressBarFill $progress={progress || 0} />
          </ProgressBarContainer>
          {statusText && <ProgressStatus>{statusText}</ProgressStatus>}
        </ProgressWrapper>
      )}
    </Container>
  );
};

export default QuirkyLoadingMessage;
